import {makeAutoObservable, observable, runInAction, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {RequestState, ModalType, ChecksumsKeys, ContestStatus, RosterPlayStatus} from "data/enums";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {
	IGamePlayer,
	type IPlayerCalculations,
	PlayerCalculations,
} from "data/utils/player.calculations";
import {compact, sumBy} from "lodash";
import type {IH2hStore, IChallenge, IInvitation} from "data/stores/h2h/h2h.store";
import {IRosterItem} from "data/types/roster";
import {Empty} from "data/types/global";
import type {IChecksumStore} from "data/stores/checksum/checksum.store";
import {useNavigate} from "react-router-dom";

interface IParams {
	userId?: number;
	navigate: ReturnType<typeof useNavigate>;
}
export interface IH2hManageGamesController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get contestId(): number;

	get isComplete(): boolean;

	get rosterStatus(): RosterPlayStatus;

	get isLoading(): boolean;

	get hasChallenge(): boolean;

	get hasActivePoolId(): boolean;

	get players(): IGamePlayer[];

	get isFullRoster(): boolean;

	get projectedPoints(): number;

	get dailyPoints(): number;

	get userRoster(): Empty<IRosterItem>;

	get challenges(): IChallenge[];

	get invitations(): IInvitation[];

	joinToPool: () => void;
}

@injectable()
export class H2hManageGamesController implements IH2hManageGamesController {
	@observable private _requestState: RequestState = RequestState.IDLE;
	@observable private _disposers: (() => void)[] = [];
	@observable private _userId?: number;
	@observable _navigate?: IParams["navigate"];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RosterStore) public readonly _rosterStore: IRosterStore,
		@inject(Bindings.ContestsStore) public readonly _contestStore: IContestsStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.ChecksumStore) public readonly _checksums: IChecksumStore,
		@inject(Bindings.PlayerCalculations)
		public readonly _playerCalculations: IPlayerCalculations
	) {
		makeAutoObservable(this);
	}

	get challenges() {
		return this._h2hStore.challenges;
	}

	get invitations(): IInvitation[] {
		return this._h2hStore.invitations;
	}

	get userRoster(): Empty<IRosterItem> {
		return this._rosterStore.currentRoster;
	}

	get isLoading(): boolean {
		return [RequestState.PENDING, RequestState.IDLE].includes(this._requestState);
	}

	get contest() {
		return this._contestStore.currentContest;
	}

	get contestId(): number {
		return this.contest?.id ?? 0;
	}

	get isComplete(): boolean {
		return this.contest?.status === ContestStatus.Complete;
	}

	get rosterStatus(): RosterPlayStatus {
		return PlayerCalculations.getCommonPlayersStatus(this.players);
	}

	get players() {
		const playerIds = this._rosterStore.rosterPlayers;
		const contest = this.contest;
		if (playerIds.length && contest) {
			return compact(
				playerIds.map((player) => {
					return this._playerCalculations.getGamePlayerById(player.playerId, contest.id);
				})
			);
		}

		return [];
	}

	get isFullRoster(): boolean {
		return this._rosterStore.isRosterFull;
	}

	get hasChallenge() {
		return !!this.challenges.find((it) => it.opponent.id === this._userId);
	}

	get hasActivePoolId(): boolean {
		return Boolean(this._h2hStore.activePoolId);
	}

	get projectedPoints(): number {
		return sumBy(this.players, (player) => player.contestStats?.projectedPoints || 0);
	}

	get dailyPoints(): number {
		return sumBy(this.players, (player) => player.contestStats?.fantasyPoints || 0);
	}

	init(params: IParams) {
		this._userId = params.userId;
		this._navigate = params.navigate;
		this._disposers = [
			this._checksums.addCallback(ChecksumsKeys.contestPlayerStats, this.fetchLive),
			this._checksums.addCallback(ChecksumsKeys.contests, this.fetchLive),
			this._checksums.addCallback(ChecksumsKeys.tournaments, this.fetchLive),
			reaction(
				() => this.contestId,
				() => this.fetchData(),
				{fireImmediately: true}
			),
		];
	}

	onChange(param: IParams) {
		this._userId = param.userId;
		this._navigate = param.navigate;
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
		const gameContestId = this._contestStore.currentGameContest?.id;
		if (gameContestId) {
			this._rosterStore.updateContestId(gameContestId);
			this._contestStore.toContestId(gameContestId);
		}
	}

	private fetchLive = async () => {
		await Promise.all([
			this._h2hStore.getChallenges(this.contestId),
			this._h2hStore.getInvitations(this.contestId),
		]);
	};
	private fetchData = () => {
		this._requestState = RequestState.PENDING;
		void Promise.all([
			this._h2hStore.getChallenges(this.contestId),
			this._h2hStore.getInvitations(this.contestId),
		])
			.then(() => {
				if (!this.hasChallenge) {
					this.toGames();
				}
			})
			.then(() => {
				runInAction(() => {
					this._requestState = RequestState.SUCCESS;
				});
			})
			.catch((e) => {
				runInAction(() => {
					this._requestState = RequestState.ERROR;
				});
				this._modalsStore.showModal(ModalType.ERROR, {
					message: extractErrorMessage(e),
				});
				trackSentryErrors(e, {}, "h2h.fetchData");
			});
	};

	joinToPool = () => {
		this._modalsStore.showModal(ModalType.H2H_CHALLENGE);
	};

	private toGames = () => {
		this._navigate?.("/head_to_head/games");
	};
}
